import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { LoginRequest, RegisterRequest, User, AuthResponse } from '../types/auth';
import { Vehicle, VehicleCatalogMake, VehicleCatalogModel, VehicleCatalogModelSearchParams, VehicleDocument, VehicleSearchParams } from '../types/vehicle';
import { Company, PublicTenantCompany } from '../types/company';
import { InvoicePaymentRequest, InvoiceRecord } from '../types/invoice';
import { Reservation, ReservationCreateRequest, ReservationUpdateRequest } from '../types/reservation';
import { ReservationHistoryEvent } from '../types/reservationHistory';
import { ReservationServiceItem, ReservationServiceItemCreateRequest } from '../types/serviceItem';
import { ServiceTemplate, ServiceTemplateCreateRequest, ServiceTemplateUpdateRequest } from '../types/serviceTemplate';
import { FleetServiceTemplate, FleetServiceTemplateCreateRequest, FleetServiceTemplateUpdateRequest } from '../types/fleetServiceTemplate';
import { Employee, EmployeeCreateRequest, EmployeeUpdateRequest } from '../types/employee';
import { AppUserCreateRequest, AppUserRecord, AppUserUpdateRequest } from '../types/appUser';
import {
  CustomerAccountRecord,
  CustomerAccountSaveRequest,
} from '../types/customerAccount';
import { Defect, DefectCreateRequest } from '../types/defect';
import { DriverRecord, DriverSaveRequest } from '../types/driver';
import { RentalTypeRecord, RentalTypeSaveRequest } from '../types/rentalType';
import { Expense, ExpenseCreateRequest, ExpenseUpdateRequest } from '../types/expense';
import { RevenueReport, VehiclePerformanceReport, ProfitReport } from '../types/report';
import { InvoiceDocument } from '../types/invoiceDocument';
import { PaymentCreateRequest, PaymentInvoice, PaymentRecord } from '../types/payment';
import { DocumentTypeOption, ManagedDocument, ManagedDocumentUpdateRequest, PublicDocumentUploadResponse } from '../types/document';
import {
  CompanyProfileFormData,
  CompanySettingsCompany,
  CompanySettingsResponse,
  SystemSettingsFormData,
} from '../types/companySettings';
import {
  CompanyLocation,
  CompanyLocationUpsertRequest,
  GeocodeResult,
} from '../types/companyLocation';
import { DomainSettings, DomainSettingsFormData } from '../types/domainSettings';
import { MailSettings, MailSettingsRequest, MailTestSendResponse } from '../types/mailSettings';
import {
  EmailPreview,
  EmailSendRequest,
  EmailSendResult,
  EmailTemplate,
  EmailTemplateRequest,
  EmailTemplateType,
  ReservationStatusEmailRule,
  ReservationStatusEmailRuleRequest,
} from '../types/emailTemplates';
import { EmailLog } from '../types/emailLog';
import {
  WhatsAppPreview,
  WhatsAppTemplate,
  WhatsAppTemplateRequest,
  WhatsAppTemplateType,
} from '../types/whatsappTemplates';
import {
  WhatsAppConnectionCheckResponse,
  WhatsAppEmbeddedSignupCompleteRequest,
  WhatsAppEmbeddedSignupConfig,
  WhatsAppLogSummaryResponse,
  WhatsAppPlatformConfig,
  WhatsAppPlatformConfigRequest,
  WhatsAppSettings,
  WhatsAppSettingsOverview,
  WhatsAppSettingsRequest,
  WhatsAppTestMessageRequest,
  WhatsAppTestMessageResponse,
} from '../types/whatsapp';
import {
  CompanyMaintenanceDashboard,
  VehicleMaintenanceOverview,
  CreateInsuranceRecordRequest,
  UpdateInsuranceRecordRequest,
  CreateInspectionRecordRequest,
  UpdateInspectionRecordRequest,
  CreateTaxRecordRequest,
  UpdateTaxRecordRequest,
  CreateServiceScheduleRequest,
  CreateServiceCompletionRequest,
  UpdateServiceCompletionRequest,
  UpdateServiceScheduleStatusRequest,
  CreateVehicleHistoryEventRequest,
  UpdateVehicleHistoryEventRequest,
  UpdateVehicleMileageRequest,
  VehicleAttachment,
  InsuranceRecord,
  InspectionRecord,
  TaxRecord,
  ServiceSchedule,
  ServiceCompletion,
  VehicleHistoryEvent,
  VehicleMileageHistoryEntry,
} from '../types/vehicleMaintenance';
import { AttentionSummary } from '../types/notification';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
  toLanguageTag,
} from '../i18n/languages';
import { APP_ROUTES, isAuthPagePath, withSearch } from '../routing/appRoutes';
import { API_BASE_URL, STATIC_BASE_URL, TENANT_COMPANY_NAME } from '../utils/runtimeUrls';

// Utility function to normalize vehicle data from backend
// Backend returns imageUrl (string), but frontend expects imageUrls (array)
function normalizeStaticUrl(url?: string | null): string {
  if (!url) return '';
  return url.startsWith('http') ? url : `${STATIC_BASE_URL}${url}`;
}

function normalizeVehicle(vehicle: any): Vehicle {
  if (!vehicle) return vehicle;
  
  // Convert imageUrl to imageUrls array
  const imageUrls: string[] = [];
  if (vehicle.imageUrl) {
    imageUrls.push(normalizeStaticUrl(vehicle.imageUrl));
  } else if (vehicle.imageUrls && Array.isArray(vehicle.imageUrls)) {
    // If imageUrls already exists, normalize each URL
    imageUrls.push(...vehicle.imageUrls.map((url: string) => 
      normalizeStaticUrl(url)
    ));
  }
  
  return {
    ...vehicle,
    imageUrls,
  };
}

// Utility function to normalize vehicle for Reservation type
// Reservation expects a simplified vehicle structure with dailyRate as number
function normalizeVehicleForReservation(vehicle: any): any {
  if (!vehicle) return vehicle;
  
  // Convert imageUrl to imageUrls array
  const imageUrls: string[] = [];
  if (vehicle.imageUrl) {
    imageUrls.push(normalizeStaticUrl(vehicle.imageUrl));
  } else if (vehicle.imageUrls && Array.isArray(vehicle.imageUrls)) {
    imageUrls.push(...vehicle.imageUrls.map((url: string) => 
      normalizeStaticUrl(url)
    ));
  }
  
  // Convert dailyRate to number if it's a string
  const dailyRate = typeof vehicle.dailyRate === 'string' 
    ? parseFloat(vehicle.dailyRate) || 0 
    : (vehicle.dailyRate || 0);
  
  // Convert type enum to string if needed
  const type = typeof vehicle.type === 'string' ? vehicle.type : (vehicle.type?.toString() || '');
  
  return {
    id: vehicle.id,
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    licensePlate: vehicle.licensePlate,
    type: type,
    dailyRate: dailyRate,
    imageUrls: imageUrls,
    companyLocationId: vehicle.companyLocationId,
    companyLocationName: vehicle.companyLocationName,
  };
}

function normalizeCompanyLocation(location: any): CompanyLocation {
  if (!location) return location;
  return normalizeBackendDatesDeep(location);
}

function normalizeVehicleDocument(document: any): VehicleDocument {
  if (!document) return document;

  const normalized = normalizeBackendDatesDeep(document);
  return {
    ...normalized,
    fileUrl: normalizeStaticUrl(normalized.fileUrl),
  };
}

function normalizeManagedDocument(document: any): ManagedDocument {
  if (!document) return document;
  return normalizeBackendDatesDeep(document);
}

function normalizeVehicleAttachment(attachment: any): VehicleAttachment {
  if (!attachment) return attachment;

  const normalized = normalizeBackendDatesDeep(attachment);
  return {
    ...normalized,
    fileUrl: normalizeStaticUrl(normalized.fileUrl),
  };
}

function normalizeVehicleMaintenanceOverview(overview: any): VehicleMaintenanceOverview {
  if (!overview) return overview;

  const normalized = normalizeBackendDatesDeep(overview);
  return {
    ...normalized,
    attachments: Array.isArray(normalized.attachments)
      ? normalized.attachments.map(normalizeVehicleAttachment)
      : [],
  };
}

function normalizePublicDocumentUploadResponse(response: any): PublicDocumentUploadResponse {
  if (!response) return response;

  const normalized = normalizeBackendDatesDeep(response);
  return {
    ...normalized,
    document: normalizeManagedDocument(normalized.document),
  };
}

function normalizeBackendDate(value: any): any {
  if (!Array.isArray(value)) {
    return value;
  }

  const [year, month, day, hour, minute, second] = value;
  if (!year || !month || !day) {
    return value;
  }

  const pad = (part: number) => String(part).padStart(2, '0');
  const date = `${year}-${pad(month)}-${pad(day)}`;

  if (value.length <= 3) {
    return date;
  }

  return `${date}T${pad(hour || 0)}:${pad(minute || 0)}:${pad(second || 0)}`;
}

function normalizeBackendDatesDeep(value: any): any {
  if (Array.isArray(value)) {
    const looksLikeDateTuple = value.length >= 3 && value.every((part) => typeof part === 'number');
    if (looksLikeDateTuple) {
      return normalizeBackendDate(value);
    }
    return value.map(normalizeBackendDatesDeep);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, normalizeBackendDatesDeep(nestedValue)])
    );
  }

  return value;
}

function normalizeReservation(reservation: any): Reservation {
  if (!reservation) return reservation;

  const rawDriver = reservation.driver;
  const rawCustomer = reservation.customer;

  return {
    ...reservation,
    startDate: normalizeBackendDate(reservation.startDate),
    endDate: normalizeBackendDate(reservation.endDate),
    actualStartDate: normalizeBackendDate(reservation.actualStartDate),
    actualEndDate: normalizeBackendDate(reservation.actualEndDate),
    pickupDate: normalizeBackendDate(reservation.pickupDate),
    returnDate: normalizeBackendDate(reservation.returnDate),
    pickupTime: normalizeBackendDate(reservation.pickupTime),
    returnTime: normalizeBackendDate(reservation.returnTime),
    createdAt: normalizeBackendDate(reservation.createdAt),
    updatedAt: normalizeBackendDate(reservation.updatedAt),
    confirmedAt: normalizeBackendDate(reservation.confirmedAt),
    cancelledAt: normalizeBackendDate(reservation.cancelledAt),
    completedAt: normalizeBackendDate(reservation.completedAt),
    driver: rawDriver ? normalizeBackendDatesDeep(rawDriver) : rawDriver,
    customer: rawCustomer ? normalizeBackendDatesDeep(rawCustomer) : rawCustomer,
    customerSameAsDriver: reservation.customerSameAsDriver ?? (rawCustomer == null),
    guestDriverName: reservation.guestDriverName,
    guestDriverEmail: reservation.guestDriverEmail,
    guestDriverPhone: reservation.guestDriverPhone,
    vehicle: reservation.vehicle ? normalizeVehicleForReservation(reservation.vehicle) : reservation.vehicle,
  };
}

function normalizePaymentInvoice(invoice: any): PaymentInvoice {
  if (!invoice) return invoice;

  const normalized = normalizeBackendDatesDeep(invoice);
  return {
    ...normalized,
    reservation: normalized.reservation ? normalizeReservation(normalized.reservation) : normalized.reservation,
  };
}

function normalizePaymentRecord(payment: any): PaymentRecord {
  if (!payment) return payment;

  const normalized = normalizeBackendDatesDeep(payment);
  return {
    ...normalized,
    invoices: Array.isArray(normalized.invoices)
      ? normalized.invoices.map(normalizePaymentInvoice)
      : [],
  };
}

function normalizeInvoiceRecord(invoice: any): InvoiceRecord {
  if (!invoice) return invoice;

  const normalized = normalizeBackendDatesDeep(invoice);
  return {
    ...normalized,
    reservation: normalized.reservation ? normalizeReservation(normalized.reservation) : normalized.reservation,
  };
}

function normalizeCustomerAccount(customerAccount: any): CustomerAccountRecord {
  if (!customerAccount) return customerAccount;
  return normalizeBackendDatesDeep(customerAccount);
}

function normalizeDriverRecord(driver: any): DriverRecord {
  if (!driver) return driver;
  return normalizeBackendDatesDeep(driver);
}

function unwrapReservationPayload(payload: any): any {
  if (payload && typeof payload === 'object' && payload.reservation) {
    return payload.reservation;
  }

  return payload;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && token !== 'null' && token !== 'undefined') {
          config.headers.Authorization = `Bearer ${token}`;
        }
        const preferredLanguage = normalizeLanguage(
          localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE
        );
        // Albanian is 'al' in our own SUPPORTED_LANGUAGES codes, but that isn't a real BCP-47
        // tag - Spring's AcceptHeaderLocaleResolver only matches it against 'sq', so sending 'al'
        // straight through silently fell back to English on every backend-rendered document
        // (contracts/invoices/order confirmations) whenever the app language was Albanian.
        // toLanguageTag() converts 'al' -> 'sq' for the wire; every other code passes through as-is.
        config.headers['Accept-Language'] = toLanguageTag(preferredLanguage);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          const isAuthCheck = requestUrl.includes('/auth/me');
          const isLoginRequest = requestUrl.includes('/auth/login');
          const isRegisterRequest = requestUrl.includes('/auth/register');
          const isAuthPage = isAuthPagePath(window.location.pathname);
          
          // Don't redirect or clear token if:
          // 1. This is the initial auth check (let AuthContext handle it)
          // 2. This is a login/register request (they handle their own errors)
          // 3. We're already on login/register page
          if (!isAuthCheck && !isLoginRequest && !isRegisterRequest && !isAuthPage) {
            // Only clear token and redirect for authenticated API calls that fail
            try {
              localStorage.removeItem('token');
            } catch (e) {
              console.error('Error removing token from localStorage:', e);
            }
            // Use replace to avoid adding to history
            const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.replace(withSearch(APP_ROUTES.auth.login, { returnUrl }));
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<{ user: User }> = await this.api.get('/auth/me');
    return response.data.user;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async changePassword(passwordData: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.api.post('/auth/change-password', passwordData);
  }

  async updateProfile(userId: number, profileData: any): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put(`/users/${userId}`, profileData);
    return response.data;
  }

  // Vehicle endpoints
  async searchVehicles(params: VehicleSearchParams): Promise<Vehicle[]> {
    const response: AxiosResponse<any> = await this.api.get('/search/vehicles', { params });
    // Backend returns { vehicles: [...], totalCount: 4, ... }
    // We need to extract just the vehicles array
    const vehicles = response.data.vehicles || response.data;
    return Array.isArray(vehicles) ? vehicles.map(normalizeVehicle) : [];
  }

  // Public, date-independent fleet browse (used to show "our fleet" before rental dates are chosen).
  async quickSearchVehicles(params: { limit?: number } = {}): Promise<Vehicle[]> {
    const response: AxiosResponse<any> = await this.api.get('/search/vehicles/quick', { params });
    const vehicles = response.data?.vehicles || [];
    return Array.isArray(vehicles) ? vehicles.map(normalizeVehicle) : [];
  }

  async getTenantCompany(): Promise<PublicTenantCompany> {
    const fromCompany = (company: Partial<PublicTenantCompany> | null | undefined) => {
      if (!company?.companyName) {
        return null;
      }

      return {
        id: company.id || 0,
        companyName: company.companyName,
        logoUrl: company.logoUrl,
        heroTagline: company.heroTagline,
        description: company.description,
        email: company.email,
        phoneNumber: company.phoneNumber,
        website: company.website,
        instagramUrl: company.instagramUrl,
        facebookUrl: company.facebookUrl,
        whatsappNumber: company.whatsappNumber,
        city: company.city,
        country: company.country,
        operatingHoursStart: company.operatingHoursStart,
        operatingHoursEnd: company.operatingHoursEnd,
        operatingDays: company.operatingDays,
        publicPageTemplate: company.publicPageTemplate,
        latitude: company.latitude,
        longitude: company.longitude,
        googleMapsApiKey: company.googleMapsApiKey,
      };
    };

    try {
      const response = await this.api.get('/search/company');
      const tenant = fromCompany(response.data);
      if (tenant) {
        return tenant;
      }
    } catch {
      // Fall through to older public endpoints.
    }

    try {
      const response = await this.api.get('/search/vehicles/quick', { params: { limit: 1 } });
      const vehicles = response.data?.vehicles || [];
      const tenant = fromCompany(vehicles[0]?.company);
      if (tenant) {
        return tenant;
      }
    } catch {
      // Fall through to filters/env fallback.
    }

    try {
      const response = await this.api.get('/search/filters');
      const tenant = fromCompany(response.data?.tenantCompany);
      if (tenant) {
        return tenant;
      }
    } catch {
      // Fall through to env fallback.
    }

    if (TENANT_COMPANY_NAME) {
      return { id: 0, companyName: TENANT_COMPANY_NAME };
    }

    throw new Error('Tenant company not available');
  }

  async getVehicle(id: string): Promise<Vehicle> {
    const response: AxiosResponse<Vehicle> = await this.api.get(`/vehicles/${id}`);
    return normalizeVehicle(response.data);
  }

  async getCompanyVehicles(companyId: string): Promise<Vehicle[]> {
    const response: AxiosResponse<Vehicle[]> = await this.api.get(`/companies/${companyId}/vehicles`);
    return Array.isArray(response.data) ? response.data.map(normalizeVehicle) : [];
  }

  async getVehicles(): Promise<Vehicle[]> {
    // Returns all vehicles for current user's company (for employees/admins)
    const response: AxiosResponse<Vehicle[]> = await this.api.get('/vehicles');
    return Array.isArray(response.data) ? response.data.map(normalizeVehicle) : [];
  }

  async getVehicleCatalogMakes(query?: string, limit: number = 50): Promise<VehicleCatalogMake[]> {
    const response: AxiosResponse<VehicleCatalogMake[]> = await this.api.get('/vehicle-catalog/makes', {
      params: { query, limit }
    });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getVehicleCatalogModels(params: VehicleCatalogModelSearchParams): Promise<VehicleCatalogModel[]> {
    const response: AxiosResponse<VehicleCatalogModel[]> = await this.api.get('/vehicle-catalog/models', {
      params: {
        makeId: params.makeId,
        makeName: params.makeName,
        query: params.query,
        limit: params.limit ?? 50,
      }
    });
    return Array.isArray(response.data) ? response.data : [];
  }

  async createVehicle(vehicleData: Partial<Vehicle>): Promise<Vehicle> {
    const response: AxiosResponse<Vehicle> = await this.api.post('/vehicles', vehicleData);
    return normalizeVehicle(response.data);
  }

  async createVehicleBulkImportDraft(data: {
    make?: string;
    model?: string;
    year?: number;
    licensePlate?: string;
    vin?: string;
    color?: string;
    fuelType?: string;
    seatingCapacity?: number;
  }): Promise<Vehicle> {
    const response: AxiosResponse<Vehicle> = await this.api.post('/vehicles/bulk-import-draft', data);
    return normalizeVehicle(response.data);
  }

  async updateVehicle(id: string, vehicleData: Partial<Vehicle>): Promise<Vehicle> {
    const response: AxiosResponse<Vehicle> = await this.api.put(`/vehicles/${id}`, vehicleData);
    return normalizeVehicle(response.data);
  }

  async deleteVehicle(id: string): Promise<void> {
    await this.api.delete(`/vehicles/${id}`);
  }

  async updateVehicleStatus(id: string, status: string): Promise<Vehicle> {
    const response: AxiosResponse<Vehicle> = await this.api.patch(`/vehicles/${id}/status`, null, { params: { status } });
    return normalizeVehicle(response.data);
  }

  async uploadVehicleImage(id: string, file: File): Promise<Vehicle> {
    const formData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse<Vehicle> = await this.api.post(`/vehicles/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return normalizeVehicle(response.data);
  }

  async getVehicleDocuments(id: string): Promise<VehicleDocument[]> {
    const response: AxiosResponse<VehicleDocument[]> = await this.api.get(`/vehicles/${id}/documents`);
    return Array.isArray(response.data) ? response.data.map(normalizeVehicleDocument) : [];
  }

  async uploadVehicleDocument(id: string, file: File): Promise<VehicleDocument> {
    const formData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse<VehicleDocument> = await this.api.post(`/vehicles/${id}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return normalizeVehicleDocument(response.data);
  }

  async deleteVehicleDocument(vehicleId: string, documentId: string): Promise<void> {
    await this.api.delete(`/vehicles/${vehicleId}/documents/${documentId}`);
  }

  async previewVehicleDocument(vehicleId: string, documentId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/vehicles/${vehicleId}/documents/${documentId}/content`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async downloadVehicleDocument(vehicleId: string, documentId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/vehicles/${vehicleId}/documents/${documentId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  // Vehicle Maintenance endpoints
  async getVehicleMaintenanceDashboard(): Promise<CompanyMaintenanceDashboard> {
    const response: AxiosResponse<CompanyMaintenanceDashboard> = await this.api.get('/vehicle-maintenance/dashboard');
    return normalizeBackendDatesDeep(response.data);
  }

  // Notifications / needs-attention summary
  async getAttentionSummary(): Promise<AttentionSummary> {
    const response: AxiosResponse<AttentionSummary> = await this.api.get('/notifications/summary');
    return normalizeBackendDatesDeep(response.data);
  }

  async getVehicleMaintenanceOverview(vehicleId: string): Promise<VehicleMaintenanceOverview> {
    const response: AxiosResponse<VehicleMaintenanceOverview> = await this.api.get(`/vehicle-maintenance/vehicles/${vehicleId}`);
    return normalizeVehicleMaintenanceOverview(response.data);
  }

  async downloadVehicleMaintenanceHistoryPdf(vehicleId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/vehicle-maintenance/vehicles/${vehicleId}/history/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async createVehicleInsuranceRecord(vehicleId: string, data: CreateInsuranceRecordRequest): Promise<InsuranceRecord> {
    const response: AxiosResponse<InsuranceRecord> = await this.api.post(`/vehicle-maintenance/vehicles/${vehicleId}/insurance`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleInsuranceRecord(vehicleId: string, recordId: string, data: UpdateInsuranceRecordRequest): Promise<InsuranceRecord> {
    const response: AxiosResponse<InsuranceRecord> = await this.api.put(`/vehicle-maintenance/vehicles/${vehicleId}/insurance/${recordId}`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async createVehicleInspectionRecord(vehicleId: string, data: CreateInspectionRecordRequest): Promise<InspectionRecord> {
    const response: AxiosResponse<InspectionRecord> = await this.api.post(`/vehicle-maintenance/vehicles/${vehicleId}/inspections`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleInspectionRecord(vehicleId: string, recordId: string, data: UpdateInspectionRecordRequest): Promise<InspectionRecord> {
    const response: AxiosResponse<InspectionRecord> = await this.api.put(`/vehicle-maintenance/vehicles/${vehicleId}/inspections/${recordId}`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async createVehicleTaxRecord(vehicleId: string, data: CreateTaxRecordRequest): Promise<TaxRecord> {
    const response: AxiosResponse<TaxRecord> = await this.api.post(`/vehicle-maintenance/vehicles/${vehicleId}/taxes`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleTaxRecord(vehicleId: string, recordId: string, data: UpdateTaxRecordRequest): Promise<TaxRecord> {
    const response: AxiosResponse<TaxRecord> = await this.api.put(`/vehicle-maintenance/vehicles/${vehicleId}/taxes/${recordId}`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async createVehicleServiceSchedule(vehicleId: string, data: CreateServiceScheduleRequest): Promise<ServiceSchedule> {
    const response: AxiosResponse<ServiceSchedule> = await this.api.post(`/vehicle-maintenance/vehicles/${vehicleId}/service-schedules`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async createVehicleServiceCompletion(vehicleId: string, data: CreateServiceCompletionRequest): Promise<ServiceCompletion> {
    const response: AxiosResponse<ServiceCompletion> = await this.api.post(`/vehicle-maintenance/vehicles/${vehicleId}/service-completions`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleServiceCompletion(vehicleId: string, completionId: string, data: UpdateServiceCompletionRequest): Promise<ServiceCompletion> {
    const response: AxiosResponse<ServiceCompletion> = await this.api.put(`/vehicle-maintenance/vehicles/${vehicleId}/service-completions/${completionId}`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleServiceScheduleStatus(scheduleId: string, data: UpdateServiceScheduleStatusRequest): Promise<ServiceSchedule> {
    const response: AxiosResponse<ServiceSchedule> = await this.api.patch(`/vehicle-maintenance/service-schedules/${scheduleId}/status`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async createVehicleHistoryEvent(vehicleId: string, data: CreateVehicleHistoryEventRequest): Promise<VehicleHistoryEvent> {
    const response: AxiosResponse<VehicleHistoryEvent> = await this.api.post(`/vehicle-maintenance/vehicles/${vehicleId}/history-events`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleHistoryEvent(vehicleId: string, eventId: string, data: UpdateVehicleHistoryEventRequest): Promise<VehicleHistoryEvent> {
    const response: AxiosResponse<VehicleHistoryEvent> = await this.api.put(`/vehicle-maintenance/vehicles/${vehicleId}/history-events/${eventId}`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateVehicleMileage(vehicleId: string, data: UpdateVehicleMileageRequest): Promise<VehicleMileageHistoryEntry> {
    const response: AxiosResponse<VehicleMileageHistoryEntry> = await this.api.patch(`/vehicle-maintenance/vehicles/${vehicleId}/mileage`, data);
    return normalizeBackendDatesDeep(response.data);
  }

  async uploadVehicleMaintenanceAttachment(params: {
    vehicleId: string;
    ownerType: string;
    ownerRecordId: string;
    notes?: string;
    file: File;
    onUploadProgress?: (progressPercent: number) => void;
  }): Promise<VehicleAttachment> {
    const formData = new FormData();
    formData.append('ownerType', params.ownerType);
    formData.append('ownerRecordId', params.ownerRecordId);
    if (params.notes) {
      formData.append('notes', params.notes);
    }
    formData.append('file', params.file);

    const response: AxiosResponse<VehicleAttachment> = await this.api.post(
      `/vehicle-maintenance/vehicles/${params.vehicleId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!params.onUploadProgress || !event.total) {
            return;
          }
          params.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );

    return normalizeVehicleAttachment(response.data);
  }

  async deleteVehicleMaintenanceAttachment(vehicleId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/vehicle-maintenance/vehicles/${vehicleId}/attachments/${attachmentId}`);
  }

  async previewVehicleMaintenanceAttachment(vehicleId: string, attachmentId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/vehicle-maintenance/vehicles/${vehicleId}/attachments/${attachmentId}/content`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async downloadVehicleMaintenanceAttachment(vehicleId: string, attachmentId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/vehicle-maintenance/vehicles/${vehicleId}/attachments/${attachmentId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  // Company endpoints
  async getCompanies(): Promise<Company[]> {
    const response: AxiosResponse<Company[]> = await this.api.get('/companies');
    return response.data;
  }

  async getCompany(id: string): Promise<Company> {
    const response: AxiosResponse<Company> = await this.api.get(`/companies/${id}`);
    return response.data;
  }

  // Reservation endpoints
  async createReservation(reservationData: ReservationCreateRequest & { driverId?: number }): Promise<Reservation> {
    const response: AxiosResponse<Reservation> = await this.api.post('/reservations', reservationData);
    return normalizeReservation(response.data);
  }

  async createGuestReservation(guestReservationData: {
    driverName?: string;
    firstName: string;
    lastName: string;
    driverEmail?: string;
    driverPhone: string;
    personalIdNumber: string;
    dateOfBirth: string;
    driverLicenseNumber: string;
    licenseIssueDate: string;
    licenseExpiryDate: string;
    licenseCategories: string;
    vehicleId: number;
    startDate: string;
    endDate?: string;
    pickupTime?: string;
    returnTime?: string;
    pickupLocation?: string;
    returnLocation?: string;
    specialRequests?: string;
    insuranceIncluded?: boolean;
    additionalDriver?: boolean;
    gpsIncluded?: boolean;
    childSeatIncluded?: boolean;
    paymentMethod?: string;
    temporaryDocumentTokens?: string[];
  }): Promise<Reservation> {
    const response: AxiosResponse<Reservation> = await this.api.post('/reservations/guest', guestReservationData);
    return normalizeReservation(response.data);
  }

  async uploadPublicDocumentAndExtract(params: {
    vehicleId: number;
    documentType: string;
    frontFile: File;
    backFile?: File | null;
    onUploadProgress?: (progressPercent: number) => void;
  }): Promise<PublicDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('vehicleId', String(params.vehicleId));
    formData.append('documentType', params.documentType);
    formData.append('frontFile', params.frontFile);
    if (params.backFile) {
      formData.append('backFile', params.backFile);
    }

    const response: AxiosResponse<PublicDocumentUploadResponse> = await this.api.post(
      '/public-documents/upload-and-extract',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!params.onUploadProgress || !event.total) return;
          params.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );

    return normalizePublicDocumentUploadResponse(response.data);
  }

  async uploadDocumentAndExtract(params: {
    documentType: string;
    frontFile: File;
    backFile?: File | null;
    onUploadProgress?: (progressPercent: number) => void;
  }): Promise<PublicDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('documentType', params.documentType);
    formData.append('frontFile', params.frontFile);
    if (params.backFile) {
      formData.append('backFile', params.backFile);
    }

    const response: AxiosResponse<PublicDocumentUploadResponse> = await this.api.post(
      '/documents/extract',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!params.onUploadProgress || !event.total) return;
          params.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );

    return normalizePublicDocumentUploadResponse(response.data);
  }

  async getUserReservations(): Promise<Reservation[]> {
    const response: AxiosResponse<Reservation[]> = await this.api.get('/reservations');
    return Array.isArray(response.data) 
      ? response.data.map(normalizeReservation)
      : [];
  }

  async getReservation(id: string): Promise<Reservation> {
    const response: AxiosResponse<Reservation> = await this.api.get(`/reservations/${id}`);
    return normalizeReservation(response.data);
  }

  async getReservationDocuments(reservationId: string): Promise<ManagedDocument[]> {
    const response: AxiosResponse<ManagedDocument[]> = await this.api.get(`/documents/reservations/${reservationId}`);
    return Array.isArray(response.data)
      ? response.data.map(normalizeManagedDocument)
      : [];
  }

  async getDriverDocuments(driverId: string): Promise<ManagedDocument[]> {
    const response: AxiosResponse<ManagedDocument[]> = await this.api.get(`/documents/drivers/${driverId}`);
    return Array.isArray(response.data)
      ? response.data.map(normalizeManagedDocument)
      : [];
  }

  async getDriverLicenseDocuments(driverId: string): Promise<ManagedDocument[]> {
    const response: AxiosResponse<ManagedDocument[]> = await this.api.get(`/documents/drivers/${driverId}/license`);
    return Array.isArray(response.data)
      ? response.data.map(normalizeManagedDocument)
      : [];
  }

  async getDocumentTypes(): Promise<DocumentTypeOption[]> {
    const response: AxiosResponse<DocumentTypeOption[]> = await this.api.get('/documents/types');
    return Array.isArray(response.data)
      ? normalizeBackendDatesDeep(response.data)
      : [];
  }

  async uploadReservationDocuments(params: {
    reservationId: string;
    documentType?: string;
    description?: string;
    files: File[];
    onUploadProgress?: (progressPercent: number) => void;
  }): Promise<ManagedDocument[]> {
    const formData = new FormData();
    if (params.documentType) {
      formData.append('documentType', params.documentType);
    }
    if (params.description?.trim()) {
      formData.append('description', params.description.trim());
    }
    params.files.forEach((file) => {
      formData.append('files', file);
    });

    const response: AxiosResponse<ManagedDocument[]> = await this.api.post(
      `/documents/reservations/${params.reservationId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!params.onUploadProgress || !event.total) return;
          params.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );

    return Array.isArray(response.data)
      ? response.data.map(normalizeManagedDocument)
      : [];
  }

  async uploadDriverDocuments(params: {
    driverId: string;
    documentType?: string;
    description?: string;
    files: File[];
    onUploadProgress?: (progressPercent: number) => void;
  }): Promise<ManagedDocument[]> {
    const formData = new FormData();
    if (params.documentType) {
      formData.append('documentType', params.documentType);
    }
    if (params.description) {
      formData.append('description', params.description);
    }
    params.files.forEach((file) => formData.append('files', file));

    const response: AxiosResponse<ManagedDocument[]> = await this.api.post(
      `/documents/drivers/${params.driverId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!params.onUploadProgress || !event.total) {
            return;
          }
          params.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );

    return Array.isArray(response.data)
      ? response.data.map(normalizeManagedDocument)
      : [];
  }

  async updateManagedDocument(documentId: string, data: ManagedDocumentUpdateRequest): Promise<ManagedDocument> {
    const response: AxiosResponse<ManagedDocument> = await this.api.patch(`/documents/${documentId}`, data);
    return normalizeManagedDocument(response.data);
  }

  async deleteManagedDocument(documentId: string): Promise<void> {
    await this.api.delete(`/documents/${documentId}`);
  }

  async previewManagedDocument(documentId: string): Promise<Blob> {
    const response = await this.api.get(`/documents/${documentId}/preview`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadManagedDocument(documentId: string): Promise<Blob> {
    const response = await this.api.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async cancelReservation(id: string): Promise<Reservation> {
    const response: AxiosResponse<any> = await this.api.patch(`/reservations/${id}/cancel`);
    return normalizeReservation(unwrapReservationPayload(response.data));
  }

  async declineReservation(id: string, declineReason?: string): Promise<Reservation> {
    const response: AxiosResponse<any> = await this.api.patch(`/reservations/${id}/decline`, null, {
      params: declineReason?.trim() ? { declineReason: declineReason.trim() } : undefined,
    });
    return normalizeReservation(unwrapReservationPayload(response.data));
  }

  async updateReservation(id: string, reservationData: ReservationUpdateRequest): Promise<Reservation> {
    const response: AxiosResponse<Reservation> = await this.api.put(`/reservations/${id}`, reservationData);
    return normalizeReservation(response.data);
  }

  async getReservationHistory(reservationId: string): Promise<ReservationHistoryEvent[]> {
    const response: AxiosResponse<ReservationHistoryEvent[]> = await this.api.get(`/reservations/${reservationId}/history`);
    return Array.isArray(response.data)
      ? response.data.map(normalizeBackendDatesDeep)
      : [];
  }

  async checkVehicleAvailability(vehicleId: number, startDate: string, endDate?: string): Promise<boolean> {
    const response: AxiosResponse<{ available: boolean }> = await this.api.get('/reservations/availability', {
      params: { vehicleId, startDate, ...(endDate ? { endDate } : {}) },
    });
    return response.data.available;
  }

  // Company management endpoints
  async getCompanyDashboard(companyId: string): Promise<any> {
    const response = await this.api.get(`/companies/${companyId}/dashboard`);
    return response.data;
  }

  async getCompanyStatistics(companyId: string): Promise<any> {
    const response = await this.api.get(`/companies/${companyId}/statistics`);
    return response.data;
  }

  async getCompanyRevenue(companyId: string, startDate?: string, endDate?: string): Promise<any> {
    const params = startDate && endDate ? { startDate, endDate } : {};
    const response = await this.api.get(`/companies/${companyId}/revenue`, { params });
    return response.data;
  }

  async createCompany(companyData: any): Promise<any> {
    const response = await this.api.post('/companies', companyData);
    return response.data;
  }

  // Company settings endpoints
  async getCompanySettings(): Promise<CompanySettingsResponse> {
    const response: AxiosResponse<CompanySettingsResponse> = await this.api.get('/companies/settings');
    return response.data;
  }

  async getWhatsAppSettings(): Promise<WhatsAppSettings> {
    const response: AxiosResponse<WhatsAppSettings> = await this.api.get('/whatsapp/settings');
    return normalizeBackendDatesDeep(response.data);
  }

  async updateWhatsAppSettings(settingsData: WhatsAppSettingsRequest): Promise<WhatsAppSettings> {
    const response: AxiosResponse<WhatsAppSettings> = await this.api.put('/whatsapp/settings', settingsData);
    return normalizeBackendDatesDeep(response.data);
  }

  async getWhatsAppOverview(): Promise<WhatsAppSettingsOverview> {
    const response: AxiosResponse<WhatsAppSettingsOverview> = await this.api.get('/whatsapp/settings/overview');
    return normalizeBackendDatesDeep(response.data);
  }

  async getWhatsAppPlatformConfig(): Promise<WhatsAppPlatformConfig> {
    const response: AxiosResponse<WhatsAppPlatformConfig> = await this.api.get('/whatsapp/settings/platform');
    return normalizeBackendDatesDeep(response.data);
  }

  async updateWhatsAppPlatformConfig(
    configData: WhatsAppPlatformConfigRequest
  ): Promise<WhatsAppPlatformConfig> {
    const response: AxiosResponse<WhatsAppPlatformConfig> = await this.api.put(
      '/whatsapp/settings/platform',
      configData
    );
    return normalizeBackendDatesDeep(response.data);
  }

  async getWhatsAppEmbeddedSignupConfig(): Promise<WhatsAppEmbeddedSignupConfig> {
    const response: AxiosResponse<WhatsAppEmbeddedSignupConfig> = await this.api.get(
      '/whatsapp/settings/embedded-signup/config'
    );
    return normalizeBackendDatesDeep(response.data);
  }

  async completeWhatsAppEmbeddedSignup(
    data: WhatsAppEmbeddedSignupCompleteRequest
  ): Promise<WhatsAppConnectionCheckResponse> {
    const response: AxiosResponse<WhatsAppConnectionCheckResponse> = await this.api.post(
      '/whatsapp/settings/embedded-signup/complete',
      data
    );
    return normalizeBackendDatesDeep(response.data);
  }

  async testWhatsAppConnection(): Promise<WhatsAppConnectionCheckResponse> {
    const response: AxiosResponse<WhatsAppConnectionCheckResponse> = await this.api.post('/whatsapp/settings/test-connection');
    return normalizeBackendDatesDeep(response.data);
  }

  async refreshWhatsAppStatus(): Promise<WhatsAppConnectionCheckResponse> {
    const response: AxiosResponse<WhatsAppConnectionCheckResponse> = await this.api.post('/whatsapp/settings/refresh-status');
    return normalizeBackendDatesDeep(response.data);
  }

  async testWhatsAppConnectionById(connectionId: number): Promise<WhatsAppConnectionCheckResponse> {
    const response: AxiosResponse<WhatsAppConnectionCheckResponse> = await this.api.post(
      `/whatsapp/settings/connections/${connectionId}/test-connection`
    );
    return normalizeBackendDatesDeep(response.data);
  }

  async refreshWhatsAppStatusById(connectionId: number): Promise<WhatsAppConnectionCheckResponse> {
    const response: AxiosResponse<WhatsAppConnectionCheckResponse> = await this.api.post(
      `/whatsapp/settings/connections/${connectionId}/refresh-status`
    );
    return normalizeBackendDatesDeep(response.data);
  }

  async setWhatsAppDefaultConnection(connectionId: number): Promise<WhatsAppSettings> {
    const response: AxiosResponse<WhatsAppSettings> = await this.api.post(
      `/whatsapp/settings/connections/${connectionId}/default`
    );
    return normalizeBackendDatesDeep(response.data);
  }

  async getWhatsAppLogs(limit: number = 20, connectionId?: number): Promise<WhatsAppLogSummaryResponse> {
    const response: AxiosResponse<WhatsAppLogSummaryResponse> = await this.api.get('/whatsapp/settings/logs', {
      params: { limit, connectionId },
    });
    return normalizeBackendDatesDeep(response.data);
  }

  async sendWhatsAppTestMessage(data: WhatsAppTestMessageRequest): Promise<WhatsAppTestMessageResponse> {
    const response: AxiosResponse<WhatsAppTestMessageResponse> = await this.api.post('/whatsapp/test/message', data);
    return normalizeBackendDatesDeep(response.data);
  }

  async uploadCompanyLogo(file: File): Promise<CompanySettingsCompany> {
    const formData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse<CompanySettingsCompany> = await this.api.post('/companies/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateCompanySettings(settingsData: CompanyProfileFormData): Promise<CompanySettingsCompany> {
    const response: AxiosResponse<CompanySettingsCompany> = await this.api.put('/companies/settings', settingsData);
    return response.data;
  }

  async getDomainSettings(): Promise<DomainSettings> {
    const response: AxiosResponse<DomainSettings> = await this.api.get('/domain-settings');
    return response.data;
  }

  async updateDomainSettings(settingsData: DomainSettingsFormData): Promise<DomainSettings> {
    const response: AxiosResponse<DomainSettings> = await this.api.put('/domain-settings', settingsData);
    return response.data;
  }

  async getMailSettings(): Promise<MailSettings> {
    const response: AxiosResponse<MailSettings> = await this.api.get('/mail-settings');
    return response.data;
  }

  async updateMailSettings(settingsData: MailSettingsRequest): Promise<MailSettings> {
    const response: AxiosResponse<MailSettings> = await this.api.put('/mail-settings', settingsData);
    return response.data;
  }

  async sendMailTestEmail(recipientEmail: string): Promise<MailTestSendResponse> {
    const response: AxiosResponse<MailTestSendResponse> = await this.api.post('/mail-settings/test-send', {
      recipientEmail,
    });
    return response.data;
  }

  async getCompanyLocations(activeOnly: boolean = false): Promise<CompanyLocation[]> {
    const response: AxiosResponse<CompanyLocation[]> = await this.api.get('/company-locations', {
      params: { activeOnly },
    });
    return Array.isArray(response.data) ? response.data.map(normalizeCompanyLocation) : [];
  }

  // Public, unauthenticated version for the guest booking dialog's pickup/return dropdowns.
  async getPublicLocations(): Promise<CompanyLocation[]> {
    const response: AxiosResponse<CompanyLocation[]> = await this.api.get('/search/locations');
    return Array.isArray(response.data) ? response.data.map(normalizeCompanyLocation) : [];
  }

  async getCompanyLocation(id: string): Promise<CompanyLocation> {
    const response: AxiosResponse<CompanyLocation> = await this.api.get(`/company-locations/${id}`);
    return normalizeCompanyLocation(response.data);
  }

  async createCompanyLocation(data: CompanyLocationUpsertRequest): Promise<CompanyLocation> {
    const response: AxiosResponse<CompanyLocation> = await this.api.post('/company-locations', data);
    return normalizeCompanyLocation(response.data);
  }

  async updateCompanyLocation(id: string, data: CompanyLocationUpsertRequest): Promise<CompanyLocation> {
    const response: AxiosResponse<CompanyLocation> = await this.api.put(`/company-locations/${id}`, data);
    return normalizeCompanyLocation(response.data);
  }

  async deleteCompanyLocation(id: string): Promise<void> {
    await this.api.delete(`/company-locations/${id}`);
  }

  async reverseGeocodeLocation(lat: number, lng: number): Promise<GeocodeResult> {
    const response: AxiosResponse<GeocodeResult> = await this.api.get('/company-locations/geocode/reverse', {
      params: { lat, lng },
    });
    return response.data;
  }

  async updateSequenceNumbers(
    sequenceData: SystemSettingsFormData
  ): Promise<Partial<CompanySettingsResponse>> {
    const response: AxiosResponse<Partial<CompanySettingsResponse>> = await this.api.put(
      '/companies/settings/sequences',
      sequenceData
    );
    return response.data;
  }

  // Employee endpoints
  async getEmployees(): Promise<Employee[]> {
    const response: AxiosResponse<Employee[]> = await this.api.get('/employees');
    return Array.isArray(response.data)
      ? response.data.map(normalizeBackendDatesDeep)
      : [];
  }

  async getEmployee(id: string): Promise<Employee> {
    const response: AxiosResponse<Employee> = await this.api.get(`/employees/${id}`);
    return normalizeBackendDatesDeep(response.data);
  }

  async createEmployee(employeeData: EmployeeCreateRequest): Promise<Employee> {
    const response: AxiosResponse<Employee> = await this.api.post('/employees', employeeData);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateEmployee(id: string, employeeData: EmployeeUpdateRequest): Promise<Employee> {
    const response: AxiosResponse<Employee> = await this.api.put(`/employees/${id}`, employeeData);
    return normalizeBackendDatesDeep(response.data);
  }

  async deleteEmployee(id: string): Promise<void> {
    await this.api.delete(`/employees/${id}`);
  }

  async updateEmployeeStatus(id: string, status: string): Promise<Employee> {
    const response: AxiosResponse<Employee> = await this.api.patch(`/employees/${id}/status`, null, {
      params: { status }
    });
    return normalizeBackendDatesDeep(response.data);
  }

  async getAvailableEmployeesForAppUser(currentUserId?: number): Promise<Employee[]> {
    const response: AxiosResponse<Employee[]> = await this.api.get('/employees/available-for-user', {
      params: currentUserId ? { currentUserId } : undefined,
    });
    return Array.isArray(response.data)
      ? response.data.map(normalizeBackendDatesDeep)
      : [];
  }

  async getAppUsers(includeSuperusers?: boolean): Promise<AppUserRecord[]> {
    const response: AxiosResponse<AppUserRecord[]> = await this.api.get('/app-users', {
      params: includeSuperusers ? { includeSuperusers: true } : undefined,
    });
    return Array.isArray(response.data)
      ? response.data.map(normalizeBackendDatesDeep)
      : [];
  }

  async getAppUser(id: string): Promise<AppUserRecord> {
    const response: AxiosResponse<AppUserRecord> = await this.api.get(`/app-users/${id}`);
    return normalizeBackendDatesDeep(response.data);
  }

  async createAppUser(userData: AppUserCreateRequest): Promise<AppUserRecord> {
    const response: AxiosResponse<AppUserRecord> = await this.api.post('/app-users', userData);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateAppUser(id: string, userData: AppUserUpdateRequest): Promise<AppUserRecord> {
    const response: AxiosResponse<AppUserRecord> = await this.api.put(`/app-users/${id}`, userData);
    return normalizeBackendDatesDeep(response.data);
  }

  async updateAppUserStatus(id: string, status: string): Promise<AppUserRecord> {
    const response: AxiosResponse<AppUserRecord> = await this.api.patch(`/app-users/${id}/status`, null, {
      params: { status },
    });
    return normalizeBackendDatesDeep(response.data);
  }

  async deleteAppUser(id: string): Promise<void> {
    await this.api.delete(`/app-users/${id}`);
  }

  async getCustomerAccounts(search?: string, active?: boolean): Promise<CustomerAccountRecord[]> {
    const response: AxiosResponse<CustomerAccountRecord[]> = await this.api.get('/customer-accounts', {
      params: {
        ...(search ? { search } : {}),
        ...(active !== undefined ? { active } : {}),
      },
    });
    return Array.isArray(response.data) ? response.data.map(normalizeCustomerAccount) : [];
  }

  async getCustomerAccount(id: string): Promise<CustomerAccountRecord> {
    const response: AxiosResponse<CustomerAccountRecord> = await this.api.get(`/customer-accounts/${id}`);
    return normalizeCustomerAccount(response.data);
  }

  async getCustomerAccountDriver(customerAccountId: string, driverId: string): Promise<DriverRecord> {
    const response: AxiosResponse<DriverRecord> = await this.api.get(
      `/customer-accounts/${customerAccountId}/drivers/${driverId}`
    );
    return normalizeDriverRecord(response.data);
  }

  async createCustomerAccount(data: CustomerAccountSaveRequest): Promise<CustomerAccountRecord> {
    const response: AxiosResponse<CustomerAccountRecord> = await this.api.post('/customer-accounts', data);
    return normalizeCustomerAccount(response.data);
  }

  async updateCustomerAccount(id: string, data: CustomerAccountSaveRequest): Promise<CustomerAccountRecord> {
    const response: AxiosResponse<CustomerAccountRecord> = await this.api.put(`/customer-accounts/${id}`, data);
    return normalizeCustomerAccount(response.data);
  }

  async deleteCustomerAccount(id: string): Promise<void> {
    await this.api.delete(`/customer-accounts/${id}`);
  }

  async linkCustomerAccountDriver(
    customerAccountId: string,
    driverId: string
  ): Promise<DriverRecord> {
    const response: AxiosResponse<DriverRecord> = await this.api.post(
      `/customer-accounts/${customerAccountId}/drivers/${driverId}`
    );
    return normalizeDriverRecord(response.data);
  }

  async unlinkCustomerAccountDriver(customerAccountId: string, driverId: string): Promise<void> {
    await this.api.delete(`/customer-accounts/${customerAccountId}/drivers/${driverId}`);
  }

  async getDrivers(search?: string, status?: string): Promise<DriverRecord[]> {
    const response: AxiosResponse<DriverRecord[]> = await this.api.get('/drivers', {
      params: {
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      },
    });
    return Array.isArray(response.data) ? response.data.map(normalizeDriverRecord) : [];
  }

  async getDriver(id: string): Promise<DriverRecord> {
    const response: AxiosResponse<DriverRecord> = await this.api.get(`/drivers/${id}`);
    return normalizeDriverRecord(response.data);
  }

  async createDriver(data: DriverSaveRequest): Promise<DriverRecord> {
    const response: AxiosResponse<DriverRecord> = await this.api.post('/drivers', data);
    return normalizeDriverRecord(response.data);
  }

  async createStandaloneDriver(data: DriverSaveRequest): Promise<DriverRecord> {
    const response: AxiosResponse<DriverRecord> = await this.api.post('/drivers/standalone', data);
    return normalizeDriverRecord(response.data);
  }

  async updateDriver(id: string, data: DriverSaveRequest): Promise<DriverRecord> {
    const response: AxiosResponse<DriverRecord> = await this.api.put(`/drivers/${id}`, data);
    return normalizeDriverRecord(response.data);
  }

  async deleteDriver(id: string): Promise<void> {
    await this.api.delete(`/drivers/${id}`);
  }

  // Rental Type endpoints
  async getRentalTypes(): Promise<RentalTypeRecord[]> {
    const response: AxiosResponse<RentalTypeRecord[]> = await this.api.get('/rental-types');
    return response.data;
  }

  async getActiveRentalTypes(): Promise<RentalTypeRecord[]> {
    const response: AxiosResponse<RentalTypeRecord[]> = await this.api.get('/rental-types/active');
    return response.data;
  }

  async getRentalType(id: string): Promise<RentalTypeRecord> {
    const response: AxiosResponse<RentalTypeRecord> = await this.api.get(`/rental-types/${id}`);
    return response.data;
  }

  async createRentalType(data: RentalTypeSaveRequest): Promise<RentalTypeRecord> {
    const response: AxiosResponse<RentalTypeRecord> = await this.api.post('/rental-types', data);
    return response.data;
  }

  async updateRentalType(id: string, data: Partial<RentalTypeSaveRequest>): Promise<RentalTypeRecord> {
    const response: AxiosResponse<RentalTypeRecord> = await this.api.put(`/rental-types/${id}`, data);
    return response.data;
  }

  async deleteRentalType(id: string): Promise<void> {
    await this.api.delete(`/rental-types/${id}`);
  }

  // Service Template endpoints
  async getServiceTemplates(): Promise<ServiceTemplate[]> {
    const response: AxiosResponse<ServiceTemplate[]> = await this.api.get('/service-templates');
    return response.data;
  }

  async getActiveServiceTemplates(): Promise<ServiceTemplate[]> {
    const response: AxiosResponse<ServiceTemplate[]> = await this.api.get('/service-templates/active');
    return response.data;
  }

  async getAutoAddServiceTemplates(): Promise<ServiceTemplate[]> {
    const response: AxiosResponse<ServiceTemplate[]> = await this.api.get('/service-templates/auto-add');
    return response.data;
  }

  async getServiceTemplate(id: string): Promise<ServiceTemplate> {
    const response: AxiosResponse<ServiceTemplate> = await this.api.get(`/service-templates/${id}`);
    return response.data;
  }

  async createServiceTemplate(data: ServiceTemplateCreateRequest): Promise<ServiceTemplate> {
    const response: AxiosResponse<ServiceTemplate> = await this.api.post('/service-templates', data);
    return response.data;
  }

  async updateServiceTemplate(id: string, data: ServiceTemplateUpdateRequest): Promise<ServiceTemplate> {
    const response: AxiosResponse<ServiceTemplate> = await this.api.put(`/service-templates/${id}`, data);
    return response.data;
  }

  async deleteServiceTemplate(id: string): Promise<void> {
    await this.api.delete(`/service-templates/${id}`);
  }

  // Fleet Service Template endpoints
  async getFleetServiceTemplates(): Promise<FleetServiceTemplate[]> {
    const response: AxiosResponse<FleetServiceTemplate[]> = await this.api.get('/fleet-service-templates');
    return response.data;
  }

  async getActiveFleetServiceTemplates(): Promise<FleetServiceTemplate[]> {
    const response: AxiosResponse<FleetServiceTemplate[]> = await this.api.get('/fleet-service-templates/active');
    return response.data;
  }

  async getFleetServiceTemplate(id: string): Promise<FleetServiceTemplate> {
    const response: AxiosResponse<FleetServiceTemplate> = await this.api.get(`/fleet-service-templates/${id}`);
    return response.data;
  }

  async createFleetServiceTemplate(data: FleetServiceTemplateCreateRequest): Promise<FleetServiceTemplate> {
    const response: AxiosResponse<FleetServiceTemplate> = await this.api.post('/fleet-service-templates', data);
    return response.data;
  }

  async updateFleetServiceTemplate(id: string, data: FleetServiceTemplateUpdateRequest): Promise<FleetServiceTemplate> {
    const response: AxiosResponse<FleetServiceTemplate> = await this.api.put(`/fleet-service-templates/${id}`, data);
    return response.data;
  }

  async deleteFleetServiceTemplate(id: string): Promise<void> {
    await this.api.delete(`/fleet-service-templates/${id}`);
  }

  // Reservation endpoints - Inspection
  async startReservation(reservationId: string, data: { pickupMileage?: number; fuelLevel: string; vehicleCondition?: string }): Promise<Reservation> {
    const response: AxiosResponse<any> = await this.api.patch(`/reservations/${reservationId}/start`, null, {
      params: data
    });
    return normalizeReservation(unwrapReservationPayload(response.data));
  }

  async completeReservation(reservationId: string, data: { returnMileage?: number; fuelLevel: string; vehicleCondition?: string; additionalFees?: number; notes?: string }): Promise<Reservation> {
    const response: AxiosResponse<any> = await this.api.patch(`/reservations/${reservationId}/complete`, null, {
      params: data
    });
    return normalizeReservation(unwrapReservationPayload(response.data));
  }

  async getCompanyReservations(companyId: number): Promise<Reservation[]> {
    const response: AxiosResponse<Reservation[]> = await this.api.get(`/companies/${companyId}/reservations`);
    return Array.isArray(response.data) ? response.data.map(normalizeReservation) : [];
  }

  async getCompanyReservationsInRange(companyId: number, start: string, end: string): Promise<Reservation[]> {
    const response: AxiosResponse<Reservation[]> = await this.api.get(`/companies/${companyId}/reservations/range`, {
      params: { start, end },
    });
    return Array.isArray(response.data) ? response.data.map(normalizeReservation) : [];
  }

  // Unbounded by date on purpose - every reservation of a single status for the company,
  // regardless of age. Backs the "Active" rental list filter (status = IN_PROGRESS).
  async getCompanyReservationsByStatus(companyId: number, status: string): Promise<Reservation[]> {
    const response: AxiosResponse<Reservation[]> = await this.api.get(`/companies/${companyId}/reservations/status/${status}`);
    return Array.isArray(response.data) ? response.data.map(normalizeReservation) : [];
  }

  async downloadOrderConfirmationPdf(id: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/reservations/${id}/order-confirmation/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadContractPdf(id: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/reservations/${id}/contract/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async previewOrderConfirmationEmail(id: string, templateId: number): Promise<EmailPreview> {
    const response: AxiosResponse<EmailPreview> = await this.api.get(`/reservations/${id}/email/preview`, {
      params: { templateId },
    });
    return response.data;
  }

  async sendOrderConfirmationEmail(id: string, request: EmailSendRequest): Promise<EmailSendResult> {
    const response: AxiosResponse<EmailSendResult> = await this.api.post(`/reservations/${id}/email/send`, request);
    return response.data;
  }

  async getOrderConfirmationEmailLogs(id: string): Promise<EmailLog[]> {
    const response: AxiosResponse<EmailLog[]> = await this.api.get(`/reservations/${id}/email/logs`);
    return Array.isArray(response.data) ? response.data.map(normalizeBackendDatesDeep) : [];
  }

  async previewReservationWhatsAppMessage(id: string, templateId: number): Promise<WhatsAppPreview> {
    const response: AxiosResponse<WhatsAppPreview> = await this.api.get(`/reservations/${id}/whatsapp/preview`, {
      params: { templateId },
    });
    return response.data;
  }

  // Reservation Service Items endpoints
  async getReservationServiceItems(reservationId: string): Promise<ReservationServiceItem[]> {
    const response: AxiosResponse<ReservationServiceItem[]> = await this.api.get(`/reservations/${reservationId}/services`);
    return response.data;
  }

  async getServiceItem(serviceItemId: string): Promise<ReservationServiceItem> {
    const response: AxiosResponse<ReservationServiceItem> = await this.api.get(`/reservations/services/${serviceItemId}`);
    return response.data;
  }

  async addServiceItem(reservationId: string, serviceItemData: ReservationServiceItemCreateRequest): Promise<ReservationServiceItem> {
    const response: AxiosResponse<{ serviceItem: ReservationServiceItem }> = await this.api.post(`/reservations/${reservationId}/services`, serviceItemData);
    return response.data.serviceItem || response.data;
  }

  async updateServiceItem(serviceItemId: string, serviceItemData: ReservationServiceItemCreateRequest): Promise<ReservationServiceItem> {
    const response: AxiosResponse<{ serviceItem: ReservationServiceItem }> = await this.api.put(`/reservations/services/${serviceItemId}`, serviceItemData);
    return response.data.serviceItem || response.data;
  }

  async deleteServiceItem(serviceItemId: string): Promise<void> {
    await this.api.delete(`/reservations/services/${serviceItemId}`);
  }

  // Defect endpoints
  // Note: Defect endpoints may need to be created in backend
  // For now, we'll use a placeholder endpoint structure
  async createDefect(data: DefectCreateRequest): Promise<Defect> {
    // If DefectController exists: /api/defects
    // Otherwise, may need to be created through service directly
    const response: AxiosResponse<Defect> = await this.api.post('/defects', data);
    return response.data;
  }

  async getVehicleDefects(vehicleId: string): Promise<Defect[]> {
    const response: AxiosResponse<Defect[]> = await this.api.get(`/vehicles/${vehicleId}/defects`);
    return response.data;
  }

  // Payment endpoints
  async createPaymentIntent(reservationId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/payments/create-intent/${reservationId}`);
    return response.data;
  }

  async confirmPayment(paymentIntentId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/payments/confirm', { paymentIntentId });
    return response.data;
  }

  async setPayAtPickup(reservationId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/payments/pay-at-pickup/${reservationId}`);
    return response.data;
  }

  async getPaymentByReservation(reservationId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/payments/reservation/${reservationId}`);
    return response.data;
  }

  async getManagedPayments(): Promise<PaymentRecord[]> {
    const response: AxiosResponse<PaymentRecord[]> = await this.api.get('/payments');
    return Array.isArray(response.data) ? response.data.map(normalizePaymentRecord) : [];
  }

  async getAvailablePaymentInvoices(): Promise<PaymentInvoice[]> {
    const response: AxiosResponse<PaymentInvoice[]> = await this.api.get('/payments/available-invoices');
    return Array.isArray(response.data) ? response.data.map(normalizePaymentInvoice) : [];
  }

  async createPaymentFromInvoices(paymentData: PaymentCreateRequest): Promise<PaymentRecord> {
    const response: AxiosResponse<PaymentRecord> = await this.api.post('/payments/from-invoices', paymentData);
    return normalizePaymentRecord(response.data);
  }

  // Invoice endpoints
  async getInvoices(): Promise<InvoiceRecord[]> {
    const response: AxiosResponse<InvoiceRecord[]> = await this.api.get('/invoices');
    return Array.isArray(response.data) ? response.data.map(normalizeInvoiceRecord) : [];
  }

  async getInvoice(id: string): Promise<InvoiceRecord> {
    const response: AxiosResponse<InvoiceRecord> = await this.api.get(`/invoices/${id}`);
    return normalizeInvoiceRecord(response.data);
  }

  async getInvoiceByReservation(reservationId: string): Promise<InvoiceRecord> {
    const response: AxiosResponse<InvoiceRecord> = await this.api.get(`/invoices/reservation/${reservationId}`);
    return normalizeInvoiceRecord(response.data);
  }

  async getInvoiceDocument(id: string): Promise<InvoiceDocument> {
    const response: AxiosResponse<InvoiceDocument> = await this.api.get(`/invoices/${id}/document`);
    return response.data;
  }

  async downloadInvoicePdf(id: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async previewInvoiceEmail(id: string, templateId: number): Promise<EmailPreview> {
    const response: AxiosResponse<EmailPreview> = await this.api.get(`/invoices/${id}/email/preview`, {
      params: { templateId },
    });
    return response.data;
  }

  async sendInvoiceEmail(id: string, request: EmailSendRequest): Promise<EmailSendResult> {
    const response: AxiosResponse<EmailSendResult> = await this.api.post(`/invoices/${id}/email/send`, request);
    return response.data;
  }

  async getInvoiceEmailLogs(id: string): Promise<EmailLog[]> {
    const response: AxiosResponse<EmailLog[]> = await this.api.get(`/invoices/${id}/email/logs`);
    return Array.isArray(response.data) ? response.data.map(normalizeBackendDatesDeep) : [];
  }

  async getEmailTemplates(type?: EmailTemplateType): Promise<EmailTemplate[]> {
    const response: AxiosResponse<EmailTemplate[]> = await this.api.get('/email-templates', {
      params: type ? { type } : undefined,
    });
    return response.data;
  }

  async createEmailTemplate(request: EmailTemplateRequest): Promise<EmailTemplate> {
    const response: AxiosResponse<EmailTemplate> = await this.api.post('/email-templates', request);
    return response.data;
  }

  async updateEmailTemplate(id: number, request: EmailTemplateRequest): Promise<EmailTemplate> {
    const response: AxiosResponse<EmailTemplate> = await this.api.put(`/email-templates/${id}`, request);
    return response.data;
  }

  async deleteEmailTemplate(id: number): Promise<void> {
    await this.api.delete(`/email-templates/${id}`);
  }

  async getWhatsAppTemplates(type?: WhatsAppTemplateType): Promise<WhatsAppTemplate[]> {
    const response: AxiosResponse<WhatsAppTemplate[]> = await this.api.get('/whatsapp-templates', {
      params: type ? { type } : undefined,
    });
    return response.data;
  }

  async createWhatsAppTemplate(request: WhatsAppTemplateRequest): Promise<WhatsAppTemplate> {
    const response: AxiosResponse<WhatsAppTemplate> = await this.api.post('/whatsapp-templates', request);
    return response.data;
  }

  async updateWhatsAppTemplate(id: number, request: WhatsAppTemplateRequest): Promise<WhatsAppTemplate> {
    const response: AxiosResponse<WhatsAppTemplate> = await this.api.put(`/whatsapp-templates/${id}`, request);
    return response.data;
  }

  async deleteWhatsAppTemplate(id: number): Promise<void> {
    await this.api.delete(`/whatsapp-templates/${id}`);
  }

  async getReservationStatusEmailRules(): Promise<ReservationStatusEmailRule[]> {
    const response: AxiosResponse<ReservationStatusEmailRule[]> = await this.api.get('/reservation-status-email-rules');
    return response.data;
  }

  async saveReservationStatusEmailRules(
    requests: ReservationStatusEmailRuleRequest[]
  ): Promise<ReservationStatusEmailRule[]> {
    const response: AxiosResponse<ReservationStatusEmailRule[]> = await this.api.put(
      '/reservation-status-email-rules',
      requests
    );
    return response.data;
  }

  async downloadInvoiceHtml(id: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/invoices/${id}/html?download=true`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getUninvoicedReservations(): Promise<Reservation[]> {
    const response: AxiosResponse<Reservation[]> = await this.api.get('/invoices/uninvoiced-reservations');
    return Array.isArray(response.data)
      ? response.data.map(normalizeReservation)
      : [];
  }

  async createInvoiceFromReservations(reservationIds: number[]): Promise<InvoiceRecord> {
    const response: AxiosResponse<InvoiceRecord> = await this.api.post('/invoices/from-reservations', {
      reservationIds,
    });
    return normalizeInvoiceRecord(response.data);
  }

  async updateInvoice(id: string, invoiceData: Partial<InvoiceRecord>): Promise<InvoiceRecord> {
    const response: AxiosResponse<InvoiceRecord> = await this.api.put(`/invoices/${id}`, invoiceData);
    return normalizeInvoiceRecord(response.data);
  }

  async deleteInvoice(id: string): Promise<void> {
    await this.api.delete(`/invoices/${id}`);
  }

  async markInvoiceAsPaid(id: string, paymentData: { paymentMethod: string; paymentReference?: string; notes?: string }): Promise<InvoiceRecord> {
    const response: AxiosResponse<InvoiceRecord> = await this.api.post(`/invoices/${id}/mark-paid`, paymentData);
    return normalizeInvoiceRecord(response.data);
  }

  async recordInvoicePayment(id: string, paymentData: InvoicePaymentRequest): Promise<InvoiceRecord> {
    const response: AxiosResponse<InvoiceRecord> = await this.api.post(`/invoices/${id}/payments`, paymentData);
    return normalizeInvoiceRecord(response.data);
  }

  // Expense endpoints
  async getExpenses(): Promise<Expense[]> {
    const response: AxiosResponse<Expense[]> = await this.api.get('/expenses');
    return response.data;
  }

  async getExpense(id: string): Promise<Expense> {
    const response: AxiosResponse<Expense> = await this.api.get(`/expenses/${id}`);
    return response.data;
  }

  async createExpense(expenseData: ExpenseCreateRequest): Promise<Expense> {
    const response: AxiosResponse<Expense> = await this.api.post('/expenses', expenseData);
    return response.data;
  }

  async updateExpense(id: string, expenseData: ExpenseUpdateRequest): Promise<Expense> {
    const response: AxiosResponse<Expense> = await this.api.put(`/expenses/${id}`, expenseData);
    return response.data;
  }

  async deleteExpense(id: string): Promise<void> {
    await this.api.delete(`/expenses/${id}`);
  }

  async getExpensesByDateRange(startDate: string, endDate: string): Promise<Expense[]> {
    const response: AxiosResponse<Expense[]> = await this.api.get('/expenses/date-range', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async getRevenueReport(startDate: string, endDate: string): Promise<RevenueReport> {
    const response: AxiosResponse<RevenueReport> = await this.api.get('/reports/revenue', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async getVehiclePerformanceReport(startDate: string, endDate: string): Promise<VehiclePerformanceReport> {
    const response: AxiosResponse<VehiclePerformanceReport> = await this.api.get('/reports/vehicle-performance', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async getTotalProfitReport(startDate: string, endDate: string): Promise<ProfitReport> {
    const response: AxiosResponse<ProfitReport> = await this.api.get('/reports/profit/total', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async getExpensesByCategory(category: string): Promise<Expense[]> {
    const response: AxiosResponse<Expense[]> = await this.api.get(`/expenses/category/${category}`);
    return response.data;
  }

  async getTotalExpenses(startDate: string, endDate: string): Promise<number> {
    const response: AxiosResponse<{ total: number }> = await this.api.get('/expenses/total', {
      params: { startDate, endDate }
    });
    return response.data.total;
  }

  async getTotalExpensesByCategory(category: string, startDate: string, endDate: string): Promise<number> {
    const response: AxiosResponse<{ total: number }> = await this.api.get(`/expenses/total/category/${category}`, {
      params: { startDate, endDate }
    });
    return response.data.total;
  }

  async getRecurringExpenses(): Promise<Expense[]> {
    const response: AxiosResponse<Expense[]> = await this.api.get('/expenses/recurring');
    return response.data;
  }

  async getUpcomingExpenses(daysAhead: number = 30): Promise<Expense[]> {
    const response: AxiosResponse<Expense[]> = await this.api.get('/expenses/upcoming', {
      params: { daysAhead }
    });
    return response.data;
  }

  async getExpensesNeedingReminders(): Promise<Expense[]> {
    const response: AxiosResponse<Expense[]> = await this.api.get('/expenses/reminders');
    return response.data;
  }

  async markReminderSent(id: string): Promise<void> {
    await this.api.post(`/expenses/${id}/mark-reminder-sent`);
  }
}

export const apiService = new ApiService();

// Export individual API modules for better organization
export const authApi = {
  login: (credentials: LoginRequest) => apiService.login(credentials),
  register: (userData: RegisterRequest) => apiService.register(userData),
  getCurrentUser: () => apiService.getCurrentUser(),
  logout: () => apiService.logout(),
  changePassword: (passwordData: { currentPassword: string; newPassword: string }) => 
    apiService.changePassword(passwordData),
  updateProfile: (userId: number, profileData: any) => apiService.updateProfile(userId, profileData),
};

export const vehicleApi = {
  search: (params: VehicleSearchParams) => apiService.searchVehicles(params),
  quickSearch: (params: { limit?: number } = {}) => apiService.quickSearchVehicles(params),
  getById: (id: string) => apiService.getVehicle(id),
  getByCompany: (companyId: string) => apiService.getCompanyVehicles(companyId),
  getAll: () => apiService.getVehicles(), // Get all company vehicles (for employees)
  create: (data: Partial<Vehicle>) => apiService.createVehicle(data),
  bulkImportDraft: (data: {
    make?: string;
    model?: string;
    year?: number;
    licensePlate?: string;
    vin?: string;
    color?: string;
    fuelType?: string;
    seatingCapacity?: number;
  }) => apiService.createVehicleBulkImportDraft(data),
  update: (id: string, data: Partial<Vehicle>) => apiService.updateVehicle(id, data),
  delete: (id: string) => apiService.deleteVehicle(id),
  updateStatus: (id: string, status: string) => apiService.updateVehicleStatus(id, status),
  uploadImage: (id: string, file: File) => apiService.uploadVehicleImage(id, file),
  getDocuments: (id: string) => apiService.getVehicleDocuments(id),
  uploadDocument: (id: string, file: File) => apiService.uploadVehicleDocument(id, file),
  deleteDocument: (vehicleId: string, documentId: string) => apiService.deleteVehicleDocument(vehicleId, documentId),
  previewDocument: (vehicleId: string, documentId: string) => apiService.previewVehicleDocument(vehicleId, documentId),
  downloadDocument: (vehicleId: string, documentId: string) => apiService.downloadVehicleDocument(vehicleId, documentId),
};

export const vehicleCatalogApi = {
  getMakes: (query?: string, limit?: number) => apiService.getVehicleCatalogMakes(query, limit),
  getModels: (params: VehicleCatalogModelSearchParams) => apiService.getVehicleCatalogModels(params),
};

export const companyApi = {
  getAll: () => apiService.getCompanies(),
  getById: (id: string) => apiService.getCompany(id),
  getTenant: () => apiService.getTenantCompany(),
  getDashboard: (id: string) => apiService.getCompanyDashboard(id),
  getStatistics: (id: string) => apiService.getCompanyStatistics(id),
  getRevenue: (id: string, startDate?: string, endDate?: string) =>
    apiService.getCompanyRevenue(id, startDate, endDate),
  create: (companyData: any) => apiService.createCompany(companyData),
  getSettings: () => apiService.getCompanySettings(),
  uploadLogo: (file: File) => apiService.uploadCompanyLogo(file),
  updateSettings: (settingsData: CompanyProfileFormData) => apiService.updateCompanySettings(settingsData),
  updateSequenceNumbers: (sequenceData: SystemSettingsFormData) => 
    apiService.updateSequenceNumbers(sequenceData),
};

export const companyLocationApi = {
  getAll: (activeOnly?: boolean) => apiService.getCompanyLocations(activeOnly),
  getPublic: () => apiService.getPublicLocations(),
  getById: (id: string) => apiService.getCompanyLocation(id),
  create: (data: CompanyLocationUpsertRequest) => apiService.createCompanyLocation(data),
  update: (id: string, data: CompanyLocationUpsertRequest) => apiService.updateCompanyLocation(id, data),
  delete: (id: string) => apiService.deleteCompanyLocation(id),
  reverseGeocode: (lat: number, lng: number) => apiService.reverseGeocodeLocation(lat, lng),
};

export const domainSettingsApi = {
  getSettings: () => apiService.getDomainSettings(),
  updateSettings: (settingsData: DomainSettingsFormData) => apiService.updateDomainSettings(settingsData),
};

export const mailSettingsApi = {
  getSettings: () => apiService.getMailSettings(),
  updateSettings: (settingsData: MailSettingsRequest) => apiService.updateMailSettings(settingsData),
  sendTestEmail: (recipientEmail: string) => apiService.sendMailTestEmail(recipientEmail),
};

export const whatsappApi = {
  getSettings: () => apiService.getWhatsAppSettings(),
  getOverview: () => apiService.getWhatsAppOverview(),
  updateSettings: (settingsData: WhatsAppSettingsRequest) => apiService.updateWhatsAppSettings(settingsData),
  getPlatformConfig: () => apiService.getWhatsAppPlatformConfig(),
  updatePlatformConfig: (configData: WhatsAppPlatformConfigRequest) =>
    apiService.updateWhatsAppPlatformConfig(configData),
  getEmbeddedSignupConfig: () => apiService.getWhatsAppEmbeddedSignupConfig(),
  completeEmbeddedSignup: (data: WhatsAppEmbeddedSignupCompleteRequest) =>
    apiService.completeWhatsAppEmbeddedSignup(data),
  testConnection: () => apiService.testWhatsAppConnection(),
  testConnectionById: (connectionId: number) => apiService.testWhatsAppConnectionById(connectionId),
  refreshStatus: () => apiService.refreshWhatsAppStatus(),
  refreshStatusById: (connectionId: number) => apiService.refreshWhatsAppStatusById(connectionId),
  setDefaultConnection: (connectionId: number) => apiService.setWhatsAppDefaultConnection(connectionId),
  getLogs: (limit?: number, connectionId?: number) => apiService.getWhatsAppLogs(limit, connectionId),
  sendTestMessage: (data: WhatsAppTestMessageRequest) => apiService.sendWhatsAppTestMessage(data),
};

export const reservationApi = {
  create: (data: ReservationCreateRequest) => apiService.createReservation(data),
  createGuestReservation: (data: any) => apiService.createGuestReservation(data),
  getUserReservations: () => apiService.getUserReservations(),
  getById: (id: string) => apiService.getReservation(id),
  getDocuments: (reservationId: string) => apiService.getReservationDocuments(reservationId),
  getHistory: (reservationId: string) => apiService.getReservationHistory(reservationId),
  update: (id: string, data: ReservationUpdateRequest) => apiService.updateReservation(id, data),
  cancel: (id: string) => apiService.cancelReservation(id),
  decline: (id: string, declineReason?: string) => apiService.declineReservation(id, declineReason),
  checkAvailability: (vehicleId: number, startDate: string, endDate?: string) =>
    apiService.checkVehicleAvailability(vehicleId, startDate, endDate),
  start: (id: string, data: { pickupMileage?: number; fuelLevel: string; vehicleCondition?: string }) =>
    apiService.startReservation(id, data),
  complete: (id: string, data: { returnMileage?: number; fuelLevel: string; vehicleCondition?: string; additionalFees?: number; notes?: string }) =>
    apiService.completeReservation(id, data),
  previewEmail: (id: string, templateId: number) => apiService.previewOrderConfirmationEmail(id, templateId),
  sendEmail: (id: string, request: EmailSendRequest) => apiService.sendOrderConfirmationEmail(id, request),
  getEmailLogs: (id: string) => apiService.getOrderConfirmationEmailLogs(id),
  previewWhatsApp: (id: string, templateId: number) => apiService.previewReservationWhatsAppMessage(id, templateId),
  getCompanyReservations: (companyId: number) => apiService.getCompanyReservations(companyId),
  getCompanyReservationsInRange: (companyId: number, start: string, end: string) =>
    apiService.getCompanyReservationsInRange(companyId, start, end),
  getCompanyReservationsByStatus: (companyId: number, status: string) =>
    apiService.getCompanyReservationsByStatus(companyId, status),
  downloadOrderConfirmationPdf: (id: string) => apiService.downloadOrderConfirmationPdf(id),
  downloadContractPdf: (id: string) => apiService.downloadContractPdf(id),
  // Service Items
  getServiceItems: (reservationId: string) => apiService.getReservationServiceItems(reservationId),
  getServiceItem: (serviceItemId: string) => apiService.getServiceItem(serviceItemId),
  addServiceItem: (reservationId: string, data: ReservationServiceItemCreateRequest) => 
    apiService.addServiceItem(reservationId, data),
  updateServiceItem: (serviceItemId: string, data: ReservationServiceItemCreateRequest) => 
    apiService.updateServiceItem(serviceItemId, data),
  deleteServiceItem: (serviceItemId: string) => apiService.deleteServiceItem(serviceItemId),
};

export const documentApi = {
  uploadPublicAndExtract: (params: {
    vehicleId: number;
    documentType: string;
    frontFile: File;
    backFile?: File | null;
    onUploadProgress?: (progressPercent: number) => void;
  }) => apiService.uploadPublicDocumentAndExtract(params),
  uploadAndExtract: (params: {
    documentType: string;
    frontFile: File;
    backFile?: File | null;
    onUploadProgress?: (progressPercent: number) => void;
  }) => apiService.uploadDocumentAndExtract(params),
  getTypes: () => apiService.getDocumentTypes(),
  uploadReservationDocuments: (params: {
    reservationId: string;
    documentType?: string;
    description?: string;
    files: File[];
    onUploadProgress?: (progressPercent: number) => void;
  }) => apiService.uploadReservationDocuments(params),
  updateManagedDocument: (documentId: string, data: ManagedDocumentUpdateRequest) =>
    apiService.updateManagedDocument(documentId, data),
  deleteManagedDocument: (documentId: string) => apiService.deleteManagedDocument(documentId),
  previewManagedDocument: (documentId: string) => apiService.previewManagedDocument(documentId),
  downloadManagedDocument: (documentId: string) => apiService.downloadManagedDocument(documentId),
  getDriverLicenseDocuments: (driverId: string) => apiService.getDriverLicenseDocuments(driverId),
};

export const defectApi = {
  create: (data: DefectCreateRequest) => apiService.createDefect(data),
  getByVehicle: (vehicleId: string) => apiService.getVehicleDefects(vehicleId),
};

export const employeeApi = {
  getAll: () => apiService.getEmployees(),
  getById: (id: string) => apiService.getEmployee(id),
  create: (data: EmployeeCreateRequest) => apiService.createEmployee(data),
  update: (id: string, data: EmployeeUpdateRequest) => apiService.updateEmployee(id, data),
  delete: (id: string) => apiService.deleteEmployee(id),
  updateStatus: (id: string, status: string) => apiService.updateEmployeeStatus(id, status),
  getAvailableForAppUser: (currentUserId?: number) => apiService.getAvailableEmployeesForAppUser(currentUserId),
};

export const appUserApi = {
  getAll: (includeSuperusers?: boolean) => apiService.getAppUsers(includeSuperusers),
  getById: (id: string) => apiService.getAppUser(id),
  create: (data: AppUserCreateRequest) => apiService.createAppUser(data),
  update: (id: string, data: AppUserUpdateRequest) => apiService.updateAppUser(id, data),
  updateStatus: (id: string, status: string) => apiService.updateAppUserStatus(id, status),
  delete: (id: string) => apiService.deleteAppUser(id),
};

export const customerAccountApi = {
  getAll: (search?: string, active?: boolean) => apiService.getCustomerAccounts(search, active),
  getById: (id: string) => apiService.getCustomerAccount(id),
  getDriver: (customerAccountId: string, driverId: string) =>
    apiService.getCustomerAccountDriver(customerAccountId, driverId),
  create: (data: CustomerAccountSaveRequest) => apiService.createCustomerAccount(data),
  update: (id: string, data: CustomerAccountSaveRequest) => apiService.updateCustomerAccount(id, data),
  linkDriver: (customerAccountId: string, driverId: string) =>
    apiService.linkCustomerAccountDriver(customerAccountId, driverId),
  unlinkDriver: (customerAccountId: string, driverId: string) =>
    apiService.unlinkCustomerAccountDriver(customerAccountId, driverId),
  delete: (id: string) => apiService.deleteCustomerAccount(id),
};

export const driverApi = {
  getAll: (search?: string, status?: string) => apiService.getDrivers(search, status),
  getById: (id: string) => apiService.getDriver(id),
  create: (data: DriverSaveRequest) => apiService.createDriver(data),
  createStandalone: (data: DriverSaveRequest) => apiService.createStandaloneDriver(data),
  update: (id: string, data: DriverSaveRequest) => apiService.updateDriver(id, data),
  delete: (id: string) => apiService.deleteDriver(id),
  getDocuments: (driverId: string) => apiService.getDriverDocuments(driverId),
  uploadDocuments: (params: {
    driverId: string;
    documentType?: string;
    description?: string;
    files: File[];
    onUploadProgress?: (progressPercent: number) => void;
  }) => apiService.uploadDriverDocuments(params),
};

export const rentalTypeApi = {
  getAll: () => apiService.getRentalTypes(),
  getActive: () => apiService.getActiveRentalTypes(),
  getById: (id: string) => apiService.getRentalType(id),
  create: (data: RentalTypeSaveRequest) => apiService.createRentalType(data),
  update: (id: string, data: Partial<RentalTypeSaveRequest>) => apiService.updateRentalType(id, data),
  delete: (id: string) => apiService.deleteRentalType(id),
};

export const serviceTemplateApi = {
  getAll: () => apiService.getServiceTemplates(),
  getActive: () => apiService.getActiveServiceTemplates(),
  getAutoAdd: () => apiService.getAutoAddServiceTemplates(),
  getById: (id: string) => apiService.getServiceTemplate(id),
  create: (data: ServiceTemplateCreateRequest) => apiService.createServiceTemplate(data),
  update: (id: string, data: ServiceTemplateUpdateRequest) => apiService.updateServiceTemplate(id, data),
  delete: (id: string) => apiService.deleteServiceTemplate(id),
};

export const fleetServiceTemplateApi = {
  getAll: () => apiService.getFleetServiceTemplates(),
  getActive: () => apiService.getActiveFleetServiceTemplates(),
  getById: (id: string) => apiService.getFleetServiceTemplate(id),
  create: (data: FleetServiceTemplateCreateRequest) => apiService.createFleetServiceTemplate(data),
  update: (id: string, data: FleetServiceTemplateUpdateRequest) => apiService.updateFleetServiceTemplate(id, data),
  delete: (id: string) => apiService.deleteFleetServiceTemplate(id),
};

export const paymentApi = {
  createIntent: (reservationId: string) => apiService.createPaymentIntent(reservationId),
  confirm: (paymentIntentId: string) => apiService.confirmPayment(paymentIntentId),
  setPayAtPickup: (reservationId: string) => apiService.setPayAtPickup(reservationId),
  getByReservation: (reservationId: string) => apiService.getPaymentByReservation(reservationId),
  getAll: () => apiService.getManagedPayments(),
  getAvailableInvoices: () => apiService.getAvailablePaymentInvoices(),
  createFromInvoices: (paymentData: PaymentCreateRequest) => apiService.createPaymentFromInvoices(paymentData),
};

export const invoiceApi = {
  getAll: () => apiService.getInvoices(),
  getById: (id: string) => apiService.getInvoice(id),
  getByReservation: (reservationId: string) => apiService.getInvoiceByReservation(reservationId),
  getDocument: (id: string) => apiService.getInvoiceDocument(id),
  downloadPdf: (id: string) => apiService.downloadInvoicePdf(id),
  downloadHtml: (id: string) => apiService.downloadInvoiceHtml(id),
  getUninvoicedReservations: () => apiService.getUninvoicedReservations(),
  createFromReservations: (reservationIds: number[]) => apiService.createInvoiceFromReservations(reservationIds),
  update: (id: string, data: Partial<any>) => apiService.updateInvoice(id, data),
  delete: (id: string) => apiService.deleteInvoice(id),
  markAsPaid: (id: string, paymentData: { paymentMethod: string; paymentReference?: string; notes?: string }) =>
    apiService.markInvoiceAsPaid(id, paymentData),
  recordPayment: (id: string, paymentData: InvoicePaymentRequest) =>
    apiService.recordInvoicePayment(id, paymentData),
  previewEmail: (id: string, templateId: number) => apiService.previewInvoiceEmail(id, templateId),
  sendEmail: (id: string, request: EmailSendRequest) => apiService.sendInvoiceEmail(id, request),
  getEmailLogs: (id: string) => apiService.getInvoiceEmailLogs(id),
};

export const emailTemplateApi = {
  getAll: (type?: EmailTemplateType) => apiService.getEmailTemplates(type),
  create: (request: EmailTemplateRequest) => apiService.createEmailTemplate(request),
  update: (id: number, request: EmailTemplateRequest) => apiService.updateEmailTemplate(id, request),
  delete: (id: number) => apiService.deleteEmailTemplate(id),
};

export const whatsappTemplateApi = {
  getAll: (type?: WhatsAppTemplateType) => apiService.getWhatsAppTemplates(type),
  create: (request: WhatsAppTemplateRequest) => apiService.createWhatsAppTemplate(request),
  update: (id: number, request: WhatsAppTemplateRequest) => apiService.updateWhatsAppTemplate(id, request),
  delete: (id: number) => apiService.deleteWhatsAppTemplate(id),
};

export const statusEmailRuleApi = {
  getAll: () => apiService.getReservationStatusEmailRules(),
  save: (rules: ReservationStatusEmailRuleRequest[]) => apiService.saveReservationStatusEmailRules(rules),
};

export const expenseApi = {
  getAll: () => apiService.getExpenses(),
  getById: (id: string) => apiService.getExpense(id),
  create: (data: ExpenseCreateRequest) => apiService.createExpense(data),
  update: (id: string, data: ExpenseUpdateRequest) => apiService.updateExpense(id, data),
  delete: (id: string) => apiService.deleteExpense(id),
  getByDateRange: (startDate: string, endDate: string) => apiService.getExpensesByDateRange(startDate, endDate),
  getByCategory: (category: string) => apiService.getExpensesByCategory(category),
  getTotal: (startDate: string, endDate: string) => apiService.getTotalExpenses(startDate, endDate),
  getTotalByCategory: (category: string, startDate: string, endDate: string) =>
    apiService.getTotalExpensesByCategory(category, startDate, endDate),
  getRecurring: () => apiService.getRecurringExpenses(),
  getUpcoming: (daysAhead?: number) => apiService.getUpcomingExpenses(daysAhead),
  getReminders: () => apiService.getExpensesNeedingReminders(),
  markReminderSent: (id: string) => apiService.markReminderSent(id),
};

export const reportApi = {
  getRevenue: (startDate: string, endDate: string) => apiService.getRevenueReport(startDate, endDate),
  getVehiclePerformance: (startDate: string, endDate: string) => apiService.getVehiclePerformanceReport(startDate, endDate),
  getTotalProfit: (startDate: string, endDate: string) => apiService.getTotalProfitReport(startDate, endDate),
};

export const vehicleMaintenanceApi = {
  getDashboard: () => apiService.getVehicleMaintenanceDashboard(),
  getOverview: (vehicleId: string) => apiService.getVehicleMaintenanceOverview(vehicleId),
  downloadHistoryPdf: (vehicleId: string) => apiService.downloadVehicleMaintenanceHistoryPdf(vehicleId),
  createInsuranceRecord: (vehicleId: string, data: CreateInsuranceRecordRequest) =>
    apiService.createVehicleInsuranceRecord(vehicleId, data),
  updateInsuranceRecord: (vehicleId: string, recordId: string, data: UpdateInsuranceRecordRequest) =>
    apiService.updateVehicleInsuranceRecord(vehicleId, recordId, data),
  createInspectionRecord: (vehicleId: string, data: CreateInspectionRecordRequest) =>
    apiService.createVehicleInspectionRecord(vehicleId, data),
  updateInspectionRecord: (vehicleId: string, recordId: string, data: UpdateInspectionRecordRequest) =>
    apiService.updateVehicleInspectionRecord(vehicleId, recordId, data),
  createTaxRecord: (vehicleId: string, data: CreateTaxRecordRequest) =>
    apiService.createVehicleTaxRecord(vehicleId, data),
  updateTaxRecord: (vehicleId: string, recordId: string, data: UpdateTaxRecordRequest) =>
    apiService.updateVehicleTaxRecord(vehicleId, recordId, data),
  createServiceSchedule: (vehicleId: string, data: CreateServiceScheduleRequest) =>
    apiService.createVehicleServiceSchedule(vehicleId, data),
  createServiceCompletion: (vehicleId: string, data: CreateServiceCompletionRequest) =>
    apiService.createVehicleServiceCompletion(vehicleId, data),
  updateServiceCompletion: (vehicleId: string, completionId: string, data: UpdateServiceCompletionRequest) =>
    apiService.updateVehicleServiceCompletion(vehicleId, completionId, data),
  updateServiceScheduleStatus: (scheduleId: string, data: UpdateServiceScheduleStatusRequest) =>
    apiService.updateVehicleServiceScheduleStatus(scheduleId, data),
  createHistoryEvent: (vehicleId: string, data: CreateVehicleHistoryEventRequest) =>
    apiService.createVehicleHistoryEvent(vehicleId, data),
  updateHistoryEvent: (vehicleId: string, eventId: string, data: UpdateVehicleHistoryEventRequest) =>
    apiService.updateVehicleHistoryEvent(vehicleId, eventId, data),
  updateMileage: (vehicleId: string, data: UpdateVehicleMileageRequest) =>
    apiService.updateVehicleMileage(vehicleId, data),
  uploadAttachment: (params: {
    vehicleId: string;
    ownerType: string;
    ownerRecordId: string;
    notes?: string;
    file: File;
    onUploadProgress?: (progressPercent: number) => void;
  }) => apiService.uploadVehicleMaintenanceAttachment(params),
  deleteAttachment: (vehicleId: string, attachmentId: string) =>
    apiService.deleteVehicleMaintenanceAttachment(vehicleId, attachmentId),
  previewAttachment: (vehicleId: string, attachmentId: string) =>
    apiService.previewVehicleMaintenanceAttachment(vehicleId, attachmentId),
  downloadAttachment: (vehicleId: string, attachmentId: string) =>
    apiService.downloadVehicleMaintenanceAttachment(vehicleId, attachmentId),
};

export const notificationApi = {
  getSummary: () => apiService.getAttentionSummary(),
};
